export var PublicationType;
(function (PublicationType) {
    PublicationType[PublicationType["Battletome"] = 0] = "Battletome";
    PublicationType[PublicationType["BattletomeSupplement"] = 1] = "BattletomeSupplement";
    PublicationType[PublicationType["FactionPack"] = 2] = "FactionPack";
    PublicationType[PublicationType["RegimentsOfRenown"] = 3] = "RegimentsOfRenown";
    PublicationType[PublicationType["PathToGlory"] = 4] = "PathToGlory";
    PublicationType[PublicationType["WhiteDwarf"] = 5] = "WhiteDwarf";
    PublicationType[PublicationType["GeneralsHandbook"] = 6] = "GeneralsHandbook";
    PublicationType[PublicationType["CoreRules"] = 7] = "CoreRules";
    PublicationType[PublicationType["Other"] = 99] = "Other";
})(PublicationType || (PublicationType = {}));
// Prefixes checked longest-first so "Battletome Supplement:" wins over "Battletome:".
const PREFIXES = [
    ['Battletome Supplement:', PublicationType.BattletomeSupplement],
    ['Battletome:', PublicationType.Battletome],
    ['Faction Pack:', PublicationType.FactionPack],
    ['Path to Glory:', PublicationType.PathToGlory],
];
export function parsePublication(id, name) {
    const base = { id, name };
    const wd = name.match(/^White Dwarf Issue (\d+)$/);
    if (wd) {
        return { ...base, type: PublicationType.WhiteDwarf, title: name, issue: Number(wd[1]) };
    }
    for (const [prefix, type] of PREFIXES) {
        if (name.startsWith(prefix)) {
            return { ...base, type, title: name.slice(prefix.length).trim() };
        }
    }
    if (name.startsWith('Regiments of Renown')) {
        return { ...base, type: PublicationType.RegimentsOfRenown, title: name };
    }
    if (name.startsWith("General's Handbook")) {
        return { ...base, type: PublicationType.GeneralsHandbook, title: name };
    }
    if (name === 'Core Rules') {
        return { ...base, type: PublicationType.CoreRules, title: name };
    }
    return { ...base, type: PublicationType.Other, title: name };
}
// Short label shown on a publication badge. White Dwarf shows its issue number
// (the meaningful identifier); everything else shows its type.
export function publicationBadgeLabel(pub) {
    if (!pub)
        return '';
    switch (pub.type) {
        case PublicationType.Battletome:
            return 'Battletome';
        case PublicationType.BattletomeSupplement:
            return 'Supplement';
        case PublicationType.FactionPack:
            return 'Faction Pack';
        case PublicationType.WhiteDwarf:
            return pub.issue ? `WD ${pub.issue}` : 'White Dwarf';
        case PublicationType.RegimentsOfRenown:
            return 'RoR';
        case PublicationType.PathToGlory:
            return 'Path to Glory';
        case PublicationType.GeneralsHandbook:
            return 'GHB';
        case PublicationType.CoreRules:
            return 'Core Rules';
        default:
            return pub.title || pub.name;
    }
}
// The publication types that identify which "book" an army belongs to.
export const ARMY_BOOK_TYPES = new Set([
    PublicationType.Battletome,
    PublicationType.BattletomeSupplement,
    PublicationType.FactionPack,
]);
// An Army of Renown's own publication may be a White Dwarf issue rather than a
// full book, so White Dwarf is allowed when deriving from an AoR's own catalog.
export const AOR_PUBLICATION_TYPES = new Set([
    ...ARMY_BOOK_TYPES,
    PublicationType.WhiteDwarf,
]);
// An army has no publication of its own; its units each carry one. Derive the
// army's book by majority vote over its units' book-type publications. This
// tolerates stray allied units (e.g. a Fyreslayers terrain piece tagged to a
// Blades of Khorne battletome) and ignores non-book sources (Scourge of Ghyran,
// Regiments of Renown). When an army references both a full battletome and a
// supplement, the more-referenced one (normally the battletome) wins.
export function deriveArmyPublication(publications, allowedTypes = ARMY_BOOK_TYPES) {
    const counts = new Map();
    for (const pub of publications) {
        if (!pub || !allowedTypes.has(pub.type))
            continue;
        const entry = counts.get(pub.id);
        if (entry)
            entry.count++;
        else
            counts.set(pub.id, { pub, count: 1 });
    }
    let winner;
    for (const entry of counts.values()) {
        if (!winner || entry.count > winner.count)
            winner = entry;
    }
    return winner?.pub;
}
